import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportLicense,
  ExportProduct,
  ExportDrawdownType,
  DrawdownSummary,
  CreateDrawdownInput,
} from 'api/ExportHubAPI';
import StyledSingleSelect from './StyledSingleSelect';
import { formatCurrency, COUNTRIES } from './licensePresets';

const DRAWDOWN_TYPES: ExportDrawdownType[] = [
  'Shipment',
  'Return',
  'Adjustment',
];

// Date picker sized for a modal field (full width), styled like the license
// detail pickers so the two read as one family.
const DATE_FIELD_SX = {
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--input-color)',
    borderRadius: '8px',
    height: '36px',
    paddingRight: '8px',
    color: 'var(--input-text-color)',
    fontFamily: 'Inter, sans-serif',
  },
  '& input': {
    color: 'var(--input-text-color)',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--es-soft-border)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--input-placeholder-color)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--action-color)',
  },
  '& .MuiSvgIcon-root': { color: 'var(--focused-text-color)' },
};

const num = (v: string): number | null => {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

// Trim float noise from quantity × unit value before it reaches the input.
const round2 = (n: number): number => Math.round(n * 100) / 100;

const rd = (l: ExportLicense | undefined, key: string): string => {
  const v = ((l?.request_details || {}) as Record<string, unknown>)[key];
  return v === null || v === undefined ? '' : String(v);
};

interface Props {
  open: boolean;
  groupId: string | undefined;
  /** Licenses this transaction may be recorded against. One entry = locked. */
  licenses: ExportLicense[];
  /** Preselect this license (defaults to the first entry). */
  licenseId?: string;
  /** Lock the product — set when opened from a product row or product detail. */
  lockedProductId?: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Record one transaction against a license (spec: per-transaction drawdown).
 *
 * This is where a product's value is actually set. There is no catalog price to
 * fall back on — unit price, quantity and terms change deal to deal — so
 * quantity × unit value is offered as a convenience and the total stays
 * editable. When the user types a total that differs, the total wins and the
 * row is flagged as overridden, because the license is annotated against the
 * invoice rather than against a formula.
 *
 * Posting past a ceiling warns and asks for a reason; it never blocks. The
 * shipment already happened, and a tool that refuses to record it just moves
 * the record into a spreadsheet.
 */
const RecordDrawdownModal: React.FC<Props> = ({
  open,
  groupId,
  licenses,
  licenseId,
  lockedProductId,
  onClose,
  onSaved,
}) => {
  const [selectedLicenseId, setSelectedLicenseId] = useState('');
  const [productId, setProductId] = useState('');
  const [type, setType] = useState<ExportDrawdownType>('Shipment');
  const [date, setDate] = useState<Date | null>(new Date());
  const [reference, setReference] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitOfMeasure, setUnitOfMeasure] = useState('');
  const [unitValue, setUnitValue] = useState('');
  const [valueDraft, setValueDraft] = useState('');
  // Whether the total was typed. Drives both the display and value_overridden.
  const [valueTouched, setValueTouched] = useState(false);
  const [eccn, setEccn] = useState('');
  const [destination, setDestination] = useState('');
  const [consignee, setConsignee] = useState('');
  const [notes, setNotes] = useState('');
  const [overdrawReason, setOverdrawReason] = useState('');

  const [products, setProducts] = useState<ExportProduct[]>([]);
  const [summary, setSummary] = useState<DrawdownSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const license = licenses.find((l) => l.id === selectedLicenseId);
  const licenseLocked = licenses.length <= 1;
  // Callers pass a fresh array literal, so depending on its identity would
  // reset the form on every parent re-render — mid-typing. Key on the ids.
  const licenseKey = licenses.map((l) => l.id).join(',');

  // Reset to a clean form each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setSelectedLicenseId(licenseId || licenses[0]?.id || '');
    setProductId(lockedProductId || '');
    setType('Shipment');
    setDate(new Date());
    setReference('');
    setQuantity('');
    setUnitValue('');
    setValueDraft('');
    setValueTouched(false);
    setNotes('');
    setOverdrawReason('');
  }, [open, licenseId, lockedProductId, licenseKey]);

  // Linked products + current consumption for whichever license is selected.
  const loadLicenseContext = useCallback(async () => {
    if (!open || !groupId || !selectedLicenseId) return;
    setLoading(true);
    try {
      const [prods, sum] = await Promise.all([
        ExportHubAPI.getLinkedProducts(selectedLicenseId, groupId),
        ExportHubAPI.getDrawdownSummary(selectedLicenseId, groupId),
      ]);
      setProducts(prods);
      setSummary(sum);
    } catch (err) {
      console.error('Failed to load license context:', err);
      setProducts([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [open, groupId, selectedLicenseId]);

  useEffect(() => {
    loadLicenseContext();
  }, [loadLicenseContext]);

  // Seed the fields the license already answers, so the user types as little as
  // possible. Re-runs when the license changes; never overwrites a typed value.
  useEffect(() => {
    if (!license) return;
    setDestination(
      rd(license, 'country_of_ultimate_destination') || rd(license, 'country'),
    );
    setConsignee(rd(license, 'customer') || rd(license, 'foreign_end_user'));
    setUnitOfMeasure(rd(license, 'unit_of_measure'));
    setEccn(license.eccns.length === 1 ? license.eccns[0] : '');
  }, [license]);

  const product = products.find((p) => p.id === productId);
  const productRollup = summary?.by_product.find(
    (r) => r.product_id === productId,
  );

  // Choosing a product refines the ECCN bucket and seeds the unit value from
  // whatever the last agreed price on this link was.
  useEffect(() => {
    if (!product) return;
    if (product.eccn && license?.eccns.includes(product.eccn)) {
      setEccn(product.eccn);
    }
    if (product.unit_of_measure) setUnitOfMeasure(product.unit_of_measure);
    const seed = productRollup?.default_unit_value;
    if (seed !== null && seed !== undefined && !unitValue) {
      setUnitValue(String(seed));
    }
    // Keyed on the resolved product and its rollup, not just the id: when the
    // modal opens from product detail the id is set immediately while the
    // linked-products and summary requests are still in flight, so keying on
    // the id alone ran this once with nothing resolved and never again. These
    // deps only change when that data arrives — not on keystrokes — so the
    // user's own edits are still safe.
  }, [productId, product?.id, productRollup?.default_unit_value]);

  // ---- the calculation ------------------------------------------------------
  const qtyNum = num(quantity);
  const unitNum = num(unitValue);
  const computed =
    qtyNum !== null && unitNum !== null ? round2(qtyNum * unitNum) : null;
  const typedNum = valueTouched ? num(valueDraft) : null;
  const magnitude = typedNum !== null ? typedNum : computed ?? 0;
  const overridden =
    typedNum !== null && computed !== null && Math.abs(computed - typedNum) > 0.005;

  // The server derives sign from type; mirror it here so the preview matches
  // exactly what posting will do.
  const signed = (n: number | null): number => {
    if (n === null) return 0;
    if (type === 'Return') return -Math.abs(n);
    if (type === 'Shipment') return Math.abs(n);
    return n;
  };
  const valueDelta = signed(magnitude);
  const qtyDelta = signed(qtyNum);

  const valueField = valueTouched
    ? valueDraft
    : computed === null
    ? ''
    : String(computed);

  // ---- impact of posting ----------------------------------------------------
  const impact = useMemo(() => {
    if (!summary) return null;
    const newPosted = summary.posted_value + valueDelta;
    const newQty = summary.posted_quantity + qtyDelta;
    const authValue = summary.authorized_value;
    const authQty = summary.authorized_quantity;
    const valueOver = authValue !== null && newPosted > authValue;
    const qtyOver = authQty !== null && newQty > authQty;
    const drawnAfter = (productRollup?.drawn_value ?? 0) + valueDelta;
    const allocation = productRollup?.allocated_value ?? 0;
    return {
      newPosted: newPosted,
      newQty: newQty,
      remaining: authValue === null ? null : authValue - newPosted,
      qtyRemaining: authQty === null ? null : authQty - newQty,
      pct:
        authValue && authValue > 0
          ? Math.min(100, Math.max(0, (newPosted / authValue) * 100))
          : 0,
      valueOver: valueOver,
      qtyOver: qtyOver,
      // The license ceilings are the regulatory ones and need an explicit
      // acknowledgement. The product allocation is an internal budget, so
      // exceeding it is worth flagging but not worth gating on.
      licenseOver: valueOver || qtyOver,
      drawnAfter: drawnAfter,
      allocation: allocation,
      allocationOver: allocation > 0 && drawnAfter > allocation,
    };
  }, [summary, valueDelta, qtyDelta, productRollup]);

  // Drafts do not decrement, so no ceiling applies to them.
  const needsReason = !!impact?.licenseOver;
  const hasAmount = Math.abs(magnitude) > 0;
  const canSubmit =
    !!groupId && !!selectedLicenseId && hasAmount && !saving && !loading;
  const canPost = canSubmit && (!needsReason || !!overdrawReason.trim());

  const submit = async (status: 'Draft' | 'Posted') => {
    if (!groupId || !selectedLicenseId) return;
    setSaving(true);
    try {
      const input: CreateDrawdownInput = {
        group_id: groupId,
        drawdown_type: type,
        status: status,
      };
      if (productId) input.product_id = productId;
      if (eccn.trim()) input.eccn = eccn.trim();
      if (reference.trim()) input.reference = reference.trim();
      if (date) input.transaction_date = moment(date).format('YYYY-MM-DD');
      // Shipment and Return get their sign from the type, so send a magnitude
      // and let the server apply it. An Adjustment carries its own sign — a
      // negative quantity there means a credit, and Math.abs would flip it into
      // a debit that contradicts the preview.
      if (qtyNum !== null) {
        input.quantity = type === 'Adjustment' ? qtyNum : Math.abs(qtyNum);
      }
      if (unitOfMeasure.trim()) input.unit_of_measure = unitOfMeasure.trim();
      if (unitNum !== null) input.unit_value = unitNum;
      // Only send a total when the user typed one — otherwise the server owns
      // the quantity × unit value calculation, so there is one formula, not two.
      if (typedNum !== null) input.value = typedNum;
      if (destination.trim()) input.destination_country = destination.trim();
      if (consignee.trim()) input.consignee = consignee.trim();
      if (notes.trim()) input.notes = notes.trim();
      if (status === 'Posted' && needsReason && overdrawReason.trim()) {
        input.overdraw_reason = overdrawReason.trim();
      }

      await ExportHubAPI.createDrawdown(selectedLicenseId, input);
      enqueueSnackbar(
        status === 'Draft'
          ? 'Saved as draft.'
          : `Posted ${formatCurrency(Math.abs(valueDelta))} against the license.`,
        { variant: 'success' },
      );
      onSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to record transaction.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const eccnOptions = license?.eccns ?? [];

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div
        className='eh-modal eh-modal-wide'
        role='dialog'
        aria-label='Record transaction'
      >
        <div className='eh-modal-head'>
          <div>
            <h2 className='eh-modal-title'>Record transaction</h2>
            {license && (
              <p className='eh-modal-subtitle eh-mono'>
                {license.license_number || license.license_type} ·{' '}
                {license.license_type} · {license.agency}
              </p>
            )}
          </div>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <div className='eh-field-row'>
            <div className='eh-field'>
              <span className='eh-field-label'>License</span>
              {licenseLocked ? (
                <div className='eh-dd-locked eh-mono'>
                  {license
                    ? `${license.license_number || license.license_type} · ${
                        license.license_type
                      }`
                    : '—'}
                </div>
              ) : (
                <StyledSingleSelect
                  options={licenses.map((l) => ({
                    value: l.id,
                    label: `${l.license_number || l.license_type} — ${
                      l.license_type
                    }`,
                  }))}
                  value={selectedLicenseId}
                  onChange={setSelectedLicenseId}
                  inline
                />
              )}
            </div>
            <div className='eh-field'>
              <span className='eh-field-label'>Product</span>
              {lockedProductId ? (
                <div className='eh-dd-locked'>
                  {products.find((p) => p.id === lockedProductId)?.name ||
                    'Selected product'}
                </div>
              ) : (
                <StyledSingleSelect
                  options={[
                    { value: '', label: 'No specific product' },
                    ...products.map((p) => ({
                      value: p.id,
                      label: p.internal_sku
                        ? `${p.name} — ${p.internal_sku}`
                        : p.name,
                    })),
                  ]}
                  value={productId}
                  onChange={setProductId}
                  inline
                />
              )}
            </div>
          </div>

          <div className='eh-field-row'>
            <div className='eh-field'>
              <span className='eh-field-label'>Type</span>
              <StyledSingleSelect
                options={DRAWDOWN_TYPES.map((t) => ({ value: t, label: t }))}
                value={type}
                onChange={(v) => setType(v as ExportDrawdownType)}
                inline
              />
            </div>
            <div className='eh-field'>
              <span className='eh-field-label'>Date</span>
              <DatePicker<Date>
                value={date}
                onChange={setDate}
                slots={{ actionBar: PickersActionBar, textField: TextField }}
                slotProps={{
                  actionBar: { actions: ['today'] },
                  textField: {
                    placeholder: 'Transaction date',
                    size: 'small',
                    sx: DATE_FIELD_SX,
                  },
                }}
              />
            </div>
            <label className='eh-field'>
              <span className='eh-field-label'>Reference</span>
              <input
                className='eh-input'
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder='Invoice #, ITN, PO'
              />
            </label>
          </div>

          {/* The crux: this transaction's own value. */}
          <div className='eh-dd-calc'>
            <label className='eh-field eh-dd-calc-field'>
              <span className='eh-field-label'>Quantity</span>
              <div className='eh-dd-qty'>
                <input
                  className='eh-input'
                  type='number'
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder='0'
                />
                <input
                  className='eh-input eh-dd-uom'
                  value={unitOfMeasure}
                  onChange={(e) => setUnitOfMeasure(e.target.value)}
                  placeholder='EA'
                  aria-label='Unit of measure'
                />
              </div>
            </label>
            <span className='eh-dd-op' aria-hidden='true'>
              ×
            </span>
            <label className='eh-field eh-dd-calc-field'>
              <span className='eh-field-label'>Unit value</span>
              <input
                className='eh-input'
                type='number'
                min={0}
                value={unitValue}
                onChange={(e) => setUnitValue(e.target.value)}
                placeholder='0.00'
              />
            </label>
            <span className='eh-dd-op' aria-hidden='true'>
              =
            </span>
            <label className='eh-field eh-dd-calc-field eh-dd-calc-result'>
              <span className='eh-field-label'>Value drawn down</span>
              <input
                className='eh-input eh-dd-total'
                type='number'
                value={valueField}
                onChange={(e) => {
                  setValueTouched(true);
                  setValueDraft(e.target.value);
                }}
                placeholder='0.00'
              />
            </label>
          </div>

          <div className='eh-dd-calc-note'>
            {overridden ? (
              <>
                <span className='eh-pill eh-pill-warning'>Overridden</span>
                <span>
                  {formatCurrency(computed)} calculated — the typed total will be
                  recorded instead.
                </span>
                <button
                  type='button'
                  className='eh-dd-link'
                  onClick={() => {
                    setValueTouched(false);
                    setValueDraft('');
                  }}
                >
                  Use the calculation
                </button>
              </>
            ) : type === 'Return' ? (
              <span>
                Returns credit value back to the license — this will be recorded
                as {formatCurrency(valueDelta)}.
              </span>
            ) : productRollup?.default_unit_value ? (
              <span>
                Last agreed unit value on this link was{' '}
                {formatCurrency(productRollup.default_unit_value)}. Edit the
                total directly to override the calculation.
              </span>
            ) : (
              <span>
                Edit the total directly when the invoice isn&apos;t a clean
                multiple.
              </span>
            )}
          </div>

          <div className='eh-field-row'>
            <div className='eh-field'>
              <span className='eh-field-label'>ECCN bucket</span>
              <StyledSingleSelect
                options={[
                  { value: '', label: 'Unassigned' },
                  ...eccnOptions.map((e) => ({ value: e, label: e })),
                ]}
                value={eccn}
                onChange={setEccn}
                inline
              />
            </div>
            <div className='eh-field'>
              <span className='eh-field-label'>Destination</span>
              <StyledSingleSelect
                options={[
                  { value: '', label: 'Not specified' },
                  ...Array.from(
                    new Set(
                      [destination, ...COUNTRIES].filter(Boolean) as string[],
                    ),
                  ).map((c) => ({ value: c, label: c })),
                ]}
                value={destination}
                onChange={setDestination}
                inline
              />
            </div>
          </div>

          <label className='eh-field'>
            <span className='eh-field-label'>Consignee / end user</span>
            <input
              className='eh-input'
              value={consignee}
              onChange={(e) => setConsignee(e.target.value)}
              placeholder='Who received this shipment'
            />
          </label>

          <label className='eh-field'>
            <span className='eh-field-label'>Notes</span>
            <textarea
              className='eh-input eh-textarea'
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='Anything worth recording against this transaction'
            />
          </label>

          {/* What posting will do, before it happens. */}
          {impact && (
            <div
              className={`eh-dd-impact ${
                impact.licenseOver
                  ? 'eh-sev-box-danger'
                  : impact.allocationOver
                  ? 'eh-sev-box-warning'
                  : ''
              }`}
            >
              <div className='eh-dd-impact-line'>
                <span className='eh-dd-impact-label'>
                  License after posting
                </span>
                <strong>
                  {impact.remaining === null
                    ? `${formatCurrency(impact.newPosted)} consumed`
                    : `${formatCurrency(impact.remaining)} remaining`}
                  {summary?.authorized_value ? (
                    <span className='eh-dd-impact-pct'>
                      {' '}
                      · {Math.round(impact.pct)}% consumed
                    </span>
                  ) : null}
                </strong>
              </div>
              <div className='eh-val-bar'>
                <span
                  className={`eh-val-bar-fill${
                    impact.valueOver
                      ? ' eh-dd-fill-over'
                      : impact.pct > 75
                      ? ' eh-dd-fill-warn'
                      : ''
                  }`}
                  style={{ width: `${impact.pct}%` }}
                />
              </div>
              {summary?.authorized_quantity !== null &&
                summary?.authorized_quantity !== undefined && (
                  <div className='eh-dd-impact-line'>
                    <span className='eh-dd-impact-label'>
                      Quantity after posting
                    </span>
                    <strong>
                      {impact.qtyRemaining} of {summary.authorized_quantity}{' '}
                      {unitOfMeasure || ''} remaining
                    </strong>
                  </div>
                )}
              {productRollup && (
                <div className='eh-dd-impact-line'>
                  <span className='eh-dd-impact-label'>
                    Product allocation after posting
                  </span>
                  <strong>
                    {formatCurrency(impact.drawnAfter)}
                    {impact.allocation > 0
                      ? ` of ${formatCurrency(impact.allocation)}`
                      : ' drawn (no allocation set)'}
                  </strong>
                </div>
              )}

              {impact.licenseOver && (
                <div className='eh-dd-warn'>
                  <WarningAmberIcon
                    className='eh-sev-danger'
                    style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
                  />
                  <span>
                    This exceeds the license&apos;s authorized{' '}
                    {impact.valueOver && impact.qtyOver
                      ? 'value and quantity'
                      : impact.valueOver
                      ? 'value'
                      : 'quantity'}
                    . You can still record it — say why, and the license will be
                    flagged as over-drawn.
                  </span>
                </div>
              )}
              {!impact.licenseOver && impact.allocationOver && (
                <div className='eh-dd-warn'>
                  <WarningAmberIcon
                    className='eh-sev-warning'
                    style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
                  />
                  <span>
                    This exceeds the planned allocation for this product, but
                    stays within the license.
                  </span>
                </div>
              )}
            </div>
          )}

          {needsReason && (
            <label className='eh-field'>
              <span className='eh-field-label'>
                Reason for over-draw <span className='eh-req'>*</span>
              </span>
              <input
                className='eh-input'
                value={overdrawReason}
                onChange={(e) => setOverdrawReason(e.target.value)}
                placeholder='e.g. amendment pending, shipped against revised value'
              />
            </label>
          )}
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-outline'
            type='button'
            onClick={() => submit('Draft')}
            disabled={!canSubmit}
            style={{ opacity: canSubmit ? 1 : 0.6 }}
          >
            Save as draft
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={() => submit('Posted')}
            disabled={!canPost}
            style={{ opacity: canPost ? 1 : 0.6 }}
          >
            {saving
              ? 'Saving…'
              : impact?.licenseOver
              ? 'Post over-draw'
              : `Post ${type.toLowerCase()}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default RecordDrawdownModal;
